// Externals imports.
import { InputAdornment, TextField, TextFieldProps } from '@material-ui/core'
import React, { MutableRefObject, ReactElement, Ref, useContext, useEffect, useMemo, useRef, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { useFormContext, UseFormMethods, ValidateResult } from '../../../../Vendor/react-hook-form/dist/index.ie11'

import { PasswordTextInput as PasswordTextInputLanguage } from '../../../../../i18n'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { IsStringEmpty, isStringNotEmpty, isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { CommonFormContext, FormContextType } from '../../Contexts/CommonFormContext'
import { ReactState, Rules } from '../../Types/Types'
import Icon, { IconName } from '../Icon'
import IconButton, { IconButtonType } from '../IconButton'
import Label from '../Label'
import { displayError } from '../../Utils/Messages'
import { EMPTY_STRING } from '../../../../NonReact/constants/Common'
import { PasswordCheckResult, PasswordPolicyComparisonResult } from '../../../../NonReact/Types/Types'
import { $debug } from '../../Utils/ConsoleUtils'
import { generatePasswordURL, passwordStrengthURL } from '../../../../NonReact/constants/ApiConstants'
import { RESTMethods } from '../../../../NonReact/constants/REST'
import { requestJSON } from '../../../../NonReact/common/API/REST/RESTClient'
import { requestGetJSON } from '../../../../NonReact/common/API/REST/RESTAPIClient'
import { AutocompleteValues } from '../../../../NonReact/constants/FormConstants'
import { JSONResponse } from '../../../../NonReact/common/Types/Types'

type PasswordGeneratorResponse = {
    password?: string
}

/**
 * Helper text properties type.
 */
type HelperTextProps = {
    helperText?: string
    error?: boolean
}

/**
 * Component properties type.
 */
export type Props = {
    className?: string
    inputName?: string
    defaultValue?: string
    value?: string
    isAdmin?: boolean
    error?: string
    helpText?: string
    placeholder?: string
    onChange?: (sNewValue: string) => void
    labelInput?: string
    labelLeft?: string
    disabled?: boolean
    isLabelHidden?: boolean
    onClick?: (event: React.MouseEvent) => void
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
    onAutoGeneratePassword?: (autoGeneratedPassword: string) => void
    autoFocus?: boolean
    onBlur?: (value?: string) => void
    isRandomiserAdornment?: boolean
    isRandomiserEnabled?: boolean
    isPasswordCheckerEnabled?: boolean
    isShowPasswordEnabled?: boolean
    defaultToRandom?: boolean
    passwordHiddenState?: ReactState<boolean>
    rules?: Rules
    alwaysShowPasswordVisibilityOption?: boolean
    tabIndex?: number
    variant?: 'standard' | 'filled' | 'outlined'
    autoComplete?: AutocompleteValues
}

const CHECK_RESULTS_ORDER: string[] = [
    'iPasswordTooShort',
    'iNotEnoughLowercaseLetters',
    'iNotEnoughUppercaseLetters',
    'iNotEnoughNumbers',
    'iNotEnoughSpecialCharacters',
    'iTooManyRepeatingCharacters',
    'iTooManySequentialCharacters'
]

// Get a random password that is always at least STRONG according to the password strength check.
export const getRandomPassword = (): Promise<Nullable<string>> =>
    requestGetJSON<PasswordGeneratorResponse>(generatePasswordURL())
        .then(
            (passwordGeneratorRes: JSONResponse<PasswordGeneratorResponse>): Nullable<string> =>
                passwordGeneratorRes?.password
        )
        .catch((e: Error): Nullable<string> => {
            displayError(e)
            return null
        })

export const comparePasswordToPolicy = (
    password?: string,
    isAdmin?: boolean
): Promise<Nullable<PasswordPolicyComparisonResult>> =>
    requestJSON<PasswordPolicyComparisonResult>(passwordStrengthURL(), {
        method: RESTMethods.POST,
        body: {
            password,
            userType: isAdmin ? 0 : 1
        }
    })
        .then(
            (
                passwordStrengthResults: JSONResponse<PasswordPolicyComparisonResult>
            ): Nullable<PasswordPolicyComparisonResult> => {
                if (passwordStrengthResults && (passwordStrengthResults?.checkResults?.length ?? 0) > 0) {
                    // Sort check results by the order defined in CHECK_RESULTS_ORDER
                    passwordStrengthResults.checkResults =
                        passwordStrengthResults?.checkResults?.sort(
                            (a: PasswordCheckResult, b: PasswordCheckResult): number =>
                                CHECK_RESULTS_ORDER.indexOf(a.checkName) - CHECK_RESULTS_ORDER.indexOf(b.checkName)
                        ) ?? []
                }

                return passwordStrengthResults
            }
        )
        .catch((e: Error): Nullable<PasswordPolicyComparisonResult> => {
            displayError(e)
            return null
        })

/**
 * Text input component.
 *
 * @param className Class name property.
 * @param inputName Input name property.
 * @param defaultValue Default value set for text input.
 * @param value Current value.
 * @param error Error message for this input.
 * @param helpText Help text for input.
 * @param onChange Change handler.
 * @param placeholder Placeholder text.
 * @param labelInput Label shown in the input.
 * @param labelLeft Label shown on the left side of the input.
 * @param disabled
 * @param isLabelHidden
 * @param elementRef
 * @param onClick
 * @param onKeyDown Callback on key down.
 * @param autoFocus Whether the input is focused.
 * @param onBlur Callback on blur.
 * @param isRandomiserAdornment A flag defining if the random password button is an end adornment.
 * @param isRandomiserEnabled A flag defining if the random password button is active.
 * @param isPasswordCheckerEnabled A flag defining if the password checked is active.
 * @param defaultToRandom Whether or not to default to a random value.
 * @param passwordHiddenState - This overrides the internal password hidden state allowing external control
 * @param rules - Validation rules for the input.
 * @param alwaysShowPasswordVisibilityOption - Always show the password visibility option.
 * @param tabIndex - Tab index for the input.
 * @param variant - Variant of the text field.
 *
 * @returns Text input component.
 */
const PasswordTextInput: React.FC<Props> = ({
    className,
    inputName,
    defaultValue,
    value,
    isAdmin,
    error,
    helpText,
    onChange,
    placeholder,
    labelInput,
    labelLeft,
    disabled,
    isLabelHidden,
    onClick,
    onKeyDown,
    onAutoGeneratePassword,
    autoFocus,
    onBlur,
    isRandomiserAdornment,
    isRandomiserEnabled,
    isPasswordCheckerEnabled,
    isShowPasswordEnabled = true,
    defaultToRandom,
    passwordHiddenState,
    rules,
    alwaysShowPasswordVisibilityOption,
    tabIndex,
    variant,
    autoComplete = AutocompleteValues.OFF
}: Props): ReactElement => {
    // Access language resources directly without context as this is top level component.
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle(PasswordTextInputLanguage.NAME)

    const commonForm: Nullable<FormContextType> = useContext(CommonFormContext)

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const [isPasswordHidden, setIsPasswordHidden]: ReactState<boolean> =
        passwordHiddenState ?? useState<boolean>(!defaultToRandom)
    const [defaultPassword, setDefaultPassword]: ReactState<Nullable<string>> = useState<Nullable<string>>()
    const [isGeneratingPassword, setIsGeneratingPassword]: ReactState<boolean> = useState<boolean>(false)
    const [passwordCheckResults, setPasswordCheckResults]: ReactState<Nullable<PasswordCheckResult[]>> =
        useState<Nullable<PasswordCheckResult[]>>()

    const elementRef: Ref<HTMLInputElement> = useRef(null)

    let methods: Definable<UseFormMethods>
    let passwordValue: Definable<string> = value

    if (inputName) {
        methods = useFormContext()

        passwordValue = methods?.watch(inputName)

        useEffect((): void => {
            if (defaultToRandom && isStringNotEmpty(defaultPassword)) {
                methods?.setValue(inputName, defaultPassword)
            }
        }, [defaultPassword])
    }

    /**
     * Prepares helper text properties.
     */
    const getHelperTextProps = (): HelperTextProps => {
        const helperTextProps: HelperTextProps = {}

        if (error) {
            helperTextProps.helperText = error
            helperTextProps.error = true
        } else if (helpText) {
            helperTextProps.helperText = helpText
        }

        return helperTextProps
    }

    /**
     * Handles change event of the text input field.
     * @param event Change event.
     */
    const handleChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        if (commonForm?.handleFormChange) {
            commonForm?.handleFormChange(inputName)
        }

        if (onChange) {
            onChange(event.target.value)
        }
    }

    /**
     * Handles onBlur event of the text input field.
     * @param event Change event.
     */
    const handleOnBlur = (event: React.FocusEvent<HTMLInputElement>): void => {
        if (onBlur) {
            onBlur(event?.target?.value)
        }
    }

    useEffect(() => {
        if (defaultToRandom && !IsStringEmpty(defaultValue)) {
            setIsGeneratingPassword(true)
            getRandomPassword()
                .then((password: Nullable<string>): void => {
                    if (isMounted.current) {
                        setDefaultPassword(password)
                    }
                })
                .catch(displayError)
                .finally((): void => {
                    if (isMounted.current) {
                        setIsGeneratingPassword(false)
                    }
                })
        } else {
            setDefaultPassword(isStringNotEmpty(defaultValue) ? defaultValue : EMPTY_STRING)
        }
    }, [])

    // This method creates a strong randomised password.
    const randomisePassword = (): void => {
        // Build strong password
        setIsGeneratingPassword(true)
        getRandomPassword()
            .then((passwordString: Nullable<string>): void => {
                if (methods && inputName) {
                    methods.setValue(inputName, passwordString)

                    setTimeout((): void => {
                        methods?.trigger(inputName)
                    })
                } else if (onChange) {
                    // If there is no react-hook-form
                    // change the value manually.
                    onChange(passwordString ?? EMPTY_STRING)
                }

                onAutoGeneratePassword?.(passwordString ?? EMPTY_STRING)
            })
            .catch(displayError)
            .finally((): void => {
                if (isMounted.current) {
                    setIsGeneratingPassword(false)
                }
            })
    }

    const labelClass: string = `label-left ${isLabelHidden ? 'visually-hidden' : ''}`

    useEffect((): void => {
        if (isPasswordCheckerEnabled) {
            methods?.trigger(inputName)
        }
    }, [isPasswordCheckerEnabled])

    const rulesFinal: Definable<Rules> = useMemo(
        (): Definable<Rules> => ({
            ...rules,
            validate: {
                ...rules?.validate,
                passwordStrength: (password: string): Definable<Promise<ValidateResult>> => {
                    if (isPasswordCheckerEnabled) {
                        if (password === defaultValue) {
                            return undefined
                        }

                        return comparePasswordToPolicy(password, isAdmin)
                            .then((result: Nullable<PasswordPolicyComparisonResult>): Definable<ValidateResult> => {
                                if (isMounted.current) {
                                    setPasswordCheckResults(result?.checkResults)
                                }

                                if (result?.passwordValid) {
                                    return undefined
                                }

                                return !!result?.checkResults?.every(
                                    (checkResult: PasswordCheckResult): boolean => checkResult.passed
                                )
                            })
                            .catch((e: Error): Definable<ValidateResult> => {
                                $debug(e)
                                return undefined
                            })
                    }

                    return undefined
                }
            }
        }),
        [rules, isAdmin, isPasswordCheckerEnabled]
    )

    const props: TextFieldProps = {
        name: inputName,
        inputRef: methods?.register ? methods.register(rulesFinal) : undefined,
        value,
        variant
    }

    return (
        <div className={`password-input ${className}`}>
            <div className='password-container'>
                {labelLeft && <Label customClass={labelClass}>{labelLeft}</Label>}
                {isVarNotEmpty(defaultPassword) ? (
                    <>
                        <div className={`input ${isRandomiserAdornment ? 'randomiser-is-adornment' : EMPTY_STRING}`}>
                            <TextField
                                {...props}
                                label={labelInput}
                                defaultValue={defaultPassword}
                                helperText={error}
                                placeholder={placeholder || undefined}
                                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                                onChange={handleChange}
                                disabled={disabled || isGeneratingPassword}
                                onClick={onClick}
                                autoComplete={autoComplete}
                                InputProps={{
                                    endAdornment: (
                                        <>
                                            {alwaysShowPasswordVisibilityOption ||
                                            (!disabled && passwordValue !== defaultPassword) ||
                                            (defaultToRandom && isShowPasswordEnabled) ? (
                                                <InputAdornment position='end' className='input-toggle'>
                                                    <IconButton
                                                        onClick={(): void => setIsPasswordHidden(!isPasswordHidden)}
                                                        iconName={
                                                            isPasswordHidden ? IconName.VISIBLE : IconName.VISIBLE_OFF
                                                        }
                                                        type={IconButtonType.BUTTON}
                                                        buttonType='button'
                                                    />
                                                </InputAdornment>
                                            ) : (
                                                <></>
                                            )}
                                            {isRandomiserEnabled && isRandomiserAdornment ? (
                                                <InputAdornment position='end' className='input-toggle'>
                                                    <div className='randomiser' tabIndex={-1}>
                                                        <IconButton
                                                            title={language.get(
                                                                PasswordTextInputLanguage.RandomiseTitle
                                                            )}
                                                            iconName={IconName.PASSWORD_RANDOMISE}
                                                            type={IconButtonType.BUTTON}
                                                            onClick={(): void => randomisePassword()}
                                                            buttonType='button'
                                                        />
                                                    </div>
                                                </InputAdornment>
                                            ) : (
                                                <></>
                                            )}
                                        </>
                                    )
                                }}
                                inputProps={{
                                    tabIndex
                                }}
                                {...getHelperTextProps()}
                                ref={elementRef}
                                type={isPasswordHidden ? 'password' : 'text'}
                                onKeyDown={onKeyDown}
                                autoFocus={autoFocus}
                                onBlur={handleOnBlur}
                            />
                        </div>
                        {isRandomiserEnabled && !isRandomiserAdornment ? (
                            <div className='randomiser' tabIndex={-1}>
                                <IconButton
                                    title={language.get(PasswordTextInputLanguage.RandomiseTitle)}
                                    iconName={IconName.PASSWORD_RANDOMISE}
                                    type={IconButtonType.BUTTON}
                                    onClick={(): void => randomisePassword()}
                                    buttonType='button'
                                />
                            </div>
                        ) : (
                            <></>
                        )}
                    </>
                ) : (
                    <></>
                )}
            </div>

            {isPasswordCheckerEnabled &&
            (passwordCheckResults?.length ?? 0) > 0 &&
            passwordValue !== defaultPassword ? (
                <div className='password-strength-info-container' tabIndex={-1}>
                    <div className='password-requirements-title' tabIndex={-1}>
                        {language.get(PasswordTextInputLanguage.PasswordRequirements_Title)}
                    </div>
                    {passwordCheckResults?.map(
                        (passwordCheckResult: PasswordCheckResult): ReactElement => (
                            <div
                                key={passwordCheckResult.checkName}
                                className={`password-strength-info-item ${
                                    passwordCheckResult.passed ? 'passed' : 'failed'
                                }`}
                                tabIndex={-1}
                            >
                                {passwordCheckResult.passed ? (
                                    <Icon iconName={IconName.CHECK} />
                                ) : (
                                    <Icon iconName={IconName.X} />
                                )}

                                <span className='psi-message' tabIndex={-1}>
                                    {passwordCheckResult.errorMessage}
                                </span>
                            </div>
                        )
                    )}
                </div>
            ) : (
                <></>
            )}
        </div>
    )
}

export default PasswordTextInput
